<template>
  <div class="privacy-policy">
    <div class="container">
      <div class="header">
        <h1>Privacy Policy</h1>
        <p class="last-updated">Last Updated: December 30, 2025</p>
        <p class="gdpr-notice">
          <strong>GDPR Compliant:</strong> This privacy policy complies with the General Data Protection Regulation (GDPR) and other applicable data protection laws.
        </p>
      </div>

      <div class="content">
        <section>
          <h2>1. Introduction</h2>
          <p>
            InvestBuddy.ai ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our portfolio management platform, including our web application, MCP (Model Context Protocol) server, API services, and third-party integrations.
          </p>
          <p>
            <strong>Data Controller:</strong> InvestBuddy.ai is the data controller responsible for your personal data.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
            <li><strong>Phone Verification:</strong> Phone number (for trial verification and security)</li>
            <li><strong>Profile Information:</strong> Investment preferences, risk tolerance, investment goals</li>
            <li><strong>Portfolio Data:</strong> Stock holdings, quantities, purchase prices, transactions</li>
            <li><strong>API Keys:</strong> Self-generated API keys for MCP server and third-party integrations (hashed and encrypted)</li>
            <li><strong>Payment Information:</strong> Processed securely by third-party payment processors (we don't store full card details)</li>
            <li><strong>Communications:</strong> Messages you send us through contact forms or support requests</li>
          </ul>

          <h3>2.2 Automatically Collected Information</h3>
          <ul>
            <li><strong>Usage Data:</strong> Pages visited, features used, time spent, click patterns</li>
            <li><strong>API Usage:</strong> API endpoint calls, request/response data, rate limit usage, error rates</li>
            <li><strong>MCP Server Activity:</strong> Tool invocations (predict, analyze, discover), request parameters, response times</li>
            <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
            <li><strong>IP Address:</strong> Your IP address for security and analytics</li>
            <li><strong>Cookies:</strong> See our <router-link to="/cookie-policy">Cookie Policy</router-link> for details</li>
            <li><strong>Log Data:</strong> Access times, error logs, performance data</li>
          </ul>

          <h3>2.2.1 Guest Mode & IP-Based Rate Limiting</h3>
          <p>
            <strong>Important:</strong> When you use our API or MCP tools through ChatGPT Actions without providing an API key (Guest Mode), we collect and temporarily store your IP address for rate limiting and abuse prevention purposes.
          </p>
          <ul>
            <li><strong>What We Collect:</strong> IP address, request timestamps, daily request count</li>
            <li><strong>Purpose:</strong> To enforce daily usage limits (5 requests/day for guests), prevent abuse, and ensure fair access for all users</li>
            <li><strong>Retention:</strong> IP address data is automatically deleted after 24 hours from last request</li>
            <li><strong>Legal Basis:</strong> Legitimate interest in preventing service abuse and ensuring system stability</li>
            <li><strong>No Tracking:</strong> We do NOT use guest IP addresses for marketing, profiling, or cross-device tracking</li>
            <li><strong>Your Rights:</strong> You can request deletion of your IP data by contacting support@investbuddy.ai</li>
          </ul>
          <p>
            To avoid IP tracking, you can <router-link to="/register">create a free account</router-link> and use your own API key. This provides the same 5 requests/day limit but uses your account ID instead of IP address for tracking.
          </p>

          <h3>2.3 Information from Third Parties</h3>
          <ul>
            <li><strong>Market Data:</strong> Stock prices, company information from financial data providers</li>
            <li><strong>OAuth Providers:</strong> If you sign in with Google, we receive basic profile information</li>
            <li><strong>News & Sentiment:</strong> Aggregated market news and sentiment data</li>
            <li><strong>AI Platform Integrations:</strong> When you use our MCP tools through ChatGPT, Claude Desktop, or other AI platforms, we receive your queries and send responses. These platforms have their own privacy policies.</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          
          <h3>3.1 Legal Bases for Processing (GDPR)</h3>
          <p>We process your personal data based on:</p>
          <ul>
            <li><strong>Contract Performance:</strong> To provide the services you requested</li>
            <li><strong>Legitimate Interests:</strong> To improve our service, prevent fraud, ensure security</li>
            <li><strong>Consent:</strong> For marketing communications, cookies (where required)</li>
            <li><strong>Legal Obligations:</strong> To comply with applicable laws and regulations</li>
          </ul>

          <h3>3.2 Specific Uses</h3>
          <ul>
            <li><strong>Service Delivery:</strong> Portfolio analysis, risk assessment, recommendations</li>
            <li><strong>MCP & API Services:</strong> Processing prediction requests, market analysis, portfolio optimization via our API and MCP server</li>
            <li><strong>Third-Party Integrations:</strong> Enabling ChatGPT, Claude Desktop, and other AI platforms to access our stock prediction and analysis tools</li>
            <li><strong>Account Management:</strong> Authentication, subscription management, support, API key generation</li>
            <li><strong>Communications:</strong> Service updates, price alerts, newsletters (with consent)</li>
            <li><strong>Personalization:</strong> Tailored recommendations based on your preferences</li>
            <li><strong>Analytics:</strong> Understanding usage patterns, API performance, ML model accuracy improvements</li>
            <li><strong>Security:</strong> Fraud prevention, abuse detection, rate limiting, API key validation, system security</li>
            <li><strong>ML Training:</strong> Improving our machine learning models (anonymized data only)</li>
            <li><strong>Compliance:</strong> Meeting legal and regulatory requirements</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal data. We may share your information with:</p>

          <h3>4.1 Service Providers</h3>
          <ul>
            <li><strong>Cloud Hosting:</strong> Railway, AWS, or similar providers</li>
            <li><strong>Email Services:</strong> For transactional emails and notifications</li>
            <li><strong>SMS Services:</strong> Twilio for phone verification</li>
            <li><strong>Payment Processors:</strong> For subscription billing</li>
            <li><strong>Market Data Providers:</strong> Twelve Data, Alpha Vantage</li>
            <li><strong>Analytics:</strong> Usage analytics and monitoring tools</li>
          </ul>
          <p>All service providers are contractually obligated to protect your data and use it only for specified purposes.</p>

          <h3>4.1.1 AI Platform Integrations</h3>
          <p>When you use InvestBuddy through third-party AI platforms, your data is subject to both our Privacy Policy and the platform's own privacy policy:</p>
          <ul>
            <li><strong>ChatGPT (OpenAI):</strong> When using our Custom GPT, your queries are sent to ChatGPT, which then calls our API. OpenAI's privacy policy applies to data in their systems.</li>
            <li><strong>Claude Desktop (Anthropic):</strong> When using our MCP server through Claude Desktop, your queries are processed locally, then sent to our API. Anthropic's privacy policy applies.</li>
            <li><strong>Other MCP Clients:</strong> Any MCP-compatible client may access our tools using your API key. Review their privacy policies.</li>
          </ul>
          <p>
            <strong>Data Collection from AI Platforms:</strong>
          </p>
          <ul>
            <li><strong>With API Key:</strong> We receive your stock queries and prediction requests. We cannot identify individual users unless they include personal information in queries.</li>
            <li><strong>Guest Mode (No API Key):</strong> We collect your IP address for rate limiting purposes as described in section 2.2.1. The AI platform cannot see your IP address.</li>
            <li><strong>What ChatGPT Sees:</strong> ChatGPT/Claude sees your questions and our API responses. They do NOT see your IP address, API key, or any data stored in our database.</li>
            <li><strong>What We See:</strong> We see the stock symbols and parameters you request, your IP address (if using Guest Mode), and your API key identifier (if logged in). We do NOT see your conversation with the AI platform.</li>
          </ul>

          <h3>4.1.2 Internal Access (Administrators)</h3>
          <p>
            Authorized InvestBuddy administrators have limited access to user data for the following purposes:
          </p>
          <ul>
            <li><strong>User Support:</strong> To assist with account issues, technical problems, and customer inquiries</li>
            <li><strong>System Administration:</strong> To maintain platform security, monitor for abuse, and ensure service quality</li>
            <li><strong>Security Monitoring:</strong> To investigate suspicious activity, fraud prevention, and security incidents</li>
          </ul>
          <p>
            <strong>What Administrators Can Access:</strong>
          </p>
          <ul>
            <li>User account information (name, email, subscription status, trial dates)</li>
            <li>Active login sessions (IP addresses, user agents, login times)</li>
            <li>API usage statistics and rate limit data</li>
            <li>System logs for debugging and security purposes</li>
          </ul>
          <p>
            <strong>Safeguards:</strong> Administrator access is restricted to essential personnel only, all access is logged for audit purposes, and administrators are bound by confidentiality agreements. We do NOT access your portfolio holdings, trading strategies, or financial data unless explicitly requested by you for support purposes.
          </p>

          <h3>4.2 Legal Requirements</h3>
          <p>We may disclose your information if required by law, court order, or governmental request, or to:</p>
          <ul>
            <li>Comply with legal obligations</li>
            <li>Protect our rights, property, or safety</li>
            <li>Prevent fraud or security issues</li>
            <li>Protect the rights and safety of our users</li>
          </ul>

          <h3>4.3 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
          </p>
        </section>

        <section>
          <h2>5. Your Rights (GDPR & Privacy Rights)</h2>
          <p>You have the following rights regarding your personal data:</p>

          <h3>5.1 Access & Portability</h3>
          <ul>
            <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
            <li><strong>Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
          </ul>

          <h3>5.2 Correction & Deletion</h3>
          <ul>
            <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
          </ul>

          <h3>5.3 Control & Restriction</h3>
          <ul>
            <li><strong>Right to Restriction:</strong> Limit how we process your data</li>
            <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing or optional processing</li>
          </ul>

          <h3>5.4 How to Exercise Your Rights</h3>
          <p>
            To exercise any of these rights, please:
          </p>
          <ul>
            <li>Email us at: support@investbuddy.ai</li>
            <li>Use the <router-link to="/contact">Contact Form</router-link></li>
            <li>Access your <router-link to="/settings">Settings Page</router-link> for account management</li>
          </ul>
          <p>
            We will respond to your request within 30 days. You also have the right to lodge a complaint with your local data protection authority.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>We retain your data for as long as necessary to provide our services and comply with legal obligations:</p>
          <ul>
            <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
            <li><strong>Deleted Accounts:</strong> Most data deleted within 30 days; some data retained for legal/security purposes</li>
            <li><strong>Guest Mode IP Addresses:</strong> Automatically deleted after 24 hours from last request (used only for rate limiting)</li>
            <li><strong>API Keys:</strong> Revoked keys deleted after 90 days; active keys retained while account is active</li>
            <li><strong>API Logs:</strong> Request/response logs retained for 90 days for debugging and security; aggregated analytics retained longer</li>
            <li><strong>Transaction Records:</strong> Retained for 7 years for accounting and legal compliance</li>
            <li><strong>Marketing Data:</strong> Removed within 30 days of unsubscribing</li>
            <li><strong>Logs & Analytics:</strong> Aggregated data retained for service improvement</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Security</h2>
          <p>We implement industry-standard security measures to protect your data:</p>
          <ul>
            <li><strong>Encryption:</strong> Data encrypted in transit (TLS/SSL) and at rest</li>
            <li><strong>Password Security:</strong> Passwords hashed with bcrypt</li>
            <li><strong>API Key Security:</strong> API keys hashed with SHA-256, never stored in plain text, transmitted via secure headers</li>
            <li><strong>Access Controls:</strong> Role-based access, principle of least privilege, API rate limiting by tier</li>
            <li><strong>Monitoring:</strong> Security monitoring, intrusion detection, API abuse detection</li>
            <li><strong>Regular Audits:</strong> Security assessments and vulnerability scanning</li>
            <li><strong>Secure Infrastructure:</strong> Hosted on secure, certified cloud platforms</li>
          </ul>
          <p>
            However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>8. Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies. For detailed information, please see our <router-link to="/cookie-policy">Cookie Policy</router-link>.
          </p>
          <p>You can manage cookie preferences through:</p>
          <ul>
            <li>Our cookie consent banner (first visit)</li>
            <li>Your browser settings</li>
            <li>Your <router-link to="/settings">Account Settings</router-link></li>
          </ul>
        </section>

        <section>
          <h2>9. International Data Transfers</h2>
          <p>
            Your data may be transferred to and processed in countries outside your country of residence. We ensure appropriate safeguards are in place:
          </p>
          <ul>
            <li>Standard Contractual Clauses (SCCs) approved by the EU Commission</li>
            <li>Adequacy decisions for countries with equivalent data protection</li>
            <li>Privacy Shield or successor frameworks (where applicable)</li>
          </ul>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            InvestBuddy.ai is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
          </p>
        </section>

        <section>
          <h2>11. Marketing Communications</h2>
          <p>
            We may send you promotional emails about new features, offers, or updates. You can opt out at any time by:
          </p>
          <ul>
            <li>Clicking "unsubscribe" in any marketing email</li>
            <li>Updating preferences in your <router-link to="/settings">Settings</router-link></li>
            <li>Contacting us at support@investbuddy.ai</li>
          </ul>
          <p>
            Note: You cannot opt out of essential service-related communications (e.g., security alerts, subscription confirmations).
          </p>
        </section>

        <section>
          <h2>12. Third-Party Links</h2>
          <p>
            Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
          </p>
        </section>

        <section>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be communicated via:
          </p>
          <ul>
            <li>Email notification to registered users</li>
            <li>Prominent notice on our platform</li>
            <li>Updated "Last Modified" date at the top of this page</li>
          </ul>
          <p>
            Continued use of the Service after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>14. Contact Us</h2>
          <p>
            For questions about this Privacy Policy or to exercise your rights, contact us:
          </p>
          <div class="contact-info">
            <p><strong>Email:</strong> support@investbuddy.ai</p>
            <p><strong>Contact Form:</strong> <router-link to="/contact">Contact Us</router-link></p>
          </div>
        </section>

      </div>

      <div class="footer-actions">
        <router-link to="/" class="btn-secondary">Back to Home</router-link>
        <router-link to="/contact" class="btn-primary">Contact Us</router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

onMounted(() => {
  window.scrollTo(0, 0)
})
</script>

<style scoped>
.privacy-policy {
  min-height: 100vh;
  background: #f9fafb;
  padding: 2rem 0;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
}

.header {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
}

.header h1 {
  font-size: 2.5rem;
  color: #111827;
  margin-bottom: 0.5rem;
}

.last-updated {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.gdpr-notice {
  background: #d1ecf1;
  border-left: 4px solid #0c5460;
  padding: 1rem;
  border-radius: 4px;
  color: #0c5460;
  margin-top: 1rem;
}

.gdpr-notice strong {
  font-weight: 600;
}

.content {
  background: #ffffff;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

section {
  margin-bottom: 2.5rem;
}

section:last-child {
  margin-bottom: 0;
}

h2 {
  font-size: 1.5rem;
  color: #111827;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

h3 {
  font-size: 1.2rem;
  color: #111827;
  margin: 1.5rem 0 0.75rem 0;
}

p {
  color: #374151;
  line-height: 1.8;
  margin-bottom: 1rem;
}

ul {
  color: #374151;
  line-height: 1.8;
  margin: 1rem 0 1rem 2rem;
}

li {
  margin-bottom: 0.5rem;
}

li strong {
  color: #111827;
}

.contact-info {
  background: #f3f4f6;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.contact-info p {
  margin-bottom: 0.5rem;
}

a {
  color: #2563eb;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.footer-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e5e7eb;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
  text-decoration: none;
  transform: translateY(-2px);
}

.btn-secondary {
  background: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f3f4f6;
  text-decoration: none;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .header h1 {
    font-size: 2rem;
  }

  .content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.3rem;
  }

  .footer-actions {
    flex-direction: column;
  }
}
</style>
